import { addDays, differenceInCalendarDays, fromUnixTime, getUnixTime, startOfDay } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { CampaignConfig } from '@marketing-milk/interfaces'
import { combineTime } from 'pages/business/campaigns/helpers/campaignForm.helpers'

/**
 * How an AE wants a stale draft's dates resolved. There is deliberately no default: only they
 * know whether the campaign is time-bound (keep the end date) or duration-bound (keep the length),
 * and the campaign carries a lifetime budget, so shortening the window silently raises the daily
 * spend rate instead of erroring.
 */
export type DateResolutionStrategy = 'keepEnd' | 'shiftWindow'

export type ResolvedDraftDates = {
  /** the start day falls before today, as calendar days in the business timezone */
  stale: boolean
  startTime: number
  endTime: number
  /** calendar days in the resulting window */
  dayCount: number
  /** false when keepEnd would invert the range */
  keepEndAvailable: boolean
}

/**
 * Single source of truth for both the preview the AE sees and the dates that go to Meta. Every
 * question about a draft's dates must come from one call of this, or the two drift.
 *
 * `now` and `timezone` are arguments rather than ambient state so day boundaries are testable.
 */
export function resolveDraftDates(
  campaign: Pick<CampaignConfig, 'startTime' | 'endTime'>,
  strategy: DateResolutionStrategy | undefined,
  now: Date,
  timezone: string
): ResolvedDraftDates {
  // Compare and rebuild everything on the business's wall clock. Bare startOfDay(fromUnixTime(..))
  // uses the browser's zone and misclassifies drafts near midnight when AE and business differ.
  const zonedStart = utcToZonedTime(fromUnixTime(campaign.startTime), timezone)
  const zonedEnd = utcToZonedTime(fromUnixTime(campaign.endTime), timezone)
  const zonedToday = utcToZonedTime(now, timezone)

  const stale = startOfDay(zonedStart) < startOfDay(zonedToday)
  const originalDayCount = differenceInCalendarDays(zonedEnd, zonedStart)

  // Both strategies start today, preserving the original wall-clock start time.
  const candidateStart = combineTime(zonedToday, zonedStart, timezone)
  // Equality counts as inverted: a draft starting 6pm and ending 5pm today would otherwise pass.
  const keepEndAvailable = candidateStart < fromUnixTime(campaign.endTime)

  if (!strategy) {
    return {
      stale,
      startTime: campaign.startTime,
      endTime: campaign.endTime,
      dayCount: originalDayCount,
      keepEndAvailable,
    }
  }

  const end =
    strategy === 'keepEnd'
      ? fromUnixTime(campaign.endTime)
      : // Same length means the same number of calendar days at the same wall-clock time, not the
        // same elapsed seconds — those diverge across a DST transition.
        combineTime(addDays(zonedToday, originalDayCount), zonedEnd, timezone)

  return {
    stale,
    startTime: getUnixTime(candidateStart),
    endTime: getUnixTime(end),
    dayCount: differenceInCalendarDays(
      utcToZonedTime(end, timezone),
      utcToZonedTime(candidateStart, timezone)
    ),
    keepEndAvailable,
  }
}
